import styles from './AWSCodePipeline.module.scss'
import Contact from '../pages/Contact'
import BestPractice from '../pages/BestPractice'
import Features from '../pages/Features'
import Introduction from '../pages/Introduction'
import Top from '../pages/Top'
import Flow from '../pages/Flow'
import PriceList from '../pages/PriceList'
import QA from '../pages/QA'
import CallToAction from '../pages/CallToAction'
import Architecture from '../pages/Architecture'
import { useRef } from 'react'
import useScrollHideContact from '../hooks/useScrollHideContact'
import MailmagazineLink from 'components/mailmagazine/MailmagazineLink/MailmagazineLink'
import NewsLp from '../pages/NewsLp'

const ContactFormId = 'bfad67c2-7ae0-433a-adc0-8e9efba644ee'

const paths = [
  { name: 'TOP', url: '/' },
  { name: 'Services', url: '/services' },
  { name: 'AWS CodePipeline', url: '#' },
]

const TopContents = {
  smallTitle: '継続的デリバリーを自動化し、イノベーションを加速',
  title: ['AWS CodePipelineで実現する', '次世代の開発フロー'],
  text: 'ソースコードの変更からリリースまで、AWSサービスと完全統合された柔軟なCI/CDパイプラインを提供します。',
  imgLink: '/images/LP/LP202407/Logo/LogoAWSCodePipeline.png',
}

const IntroductionContents = {
  title: '環境間で異なる設定や手順が存在し、一貫したデプロイメントに苦労していませんか？',
  list: [
    {
      text: ['開発環境と本番環境の設定の違いによりデプロイ時のトラブルが頻発'],
      icon: '/images/LP/LP202407/Icon/Introduction/BsExclamationTriangle.svg',
    },
    {
      text: ['複数の環境に対してそれぞれ手動で設定変更を行う必要があり時間がかかる'],
      icon: '/images/LP/LP202407/Icon/Introduction/BsHourglassSplit.svg',
    },
    {
      text: ['環境ごとの手順の違いにより、チーム間でのコミュニケーションエラーが起きる'],
      icon: '/images/LP/LP202407/Icon/Introduction/BsPersonX.svg',
    },
  ],
}
const BestPracticeContents = {
  heading: ['ワンストップCI/CD：CodePipelineで実現する', '効率的なAWS開発環境を提供します'],
  text: [
    'AWS CodePipeline導入支援サービスは、AWSクラウドの能力を最大限に引き出したCI/CDプロセスの構築を目指す企業に最適です。',
    'CodeCommit、CodeBuild、CodeDeployなど、AWSのデベロッパーツールスイートと緊密に連携し、ソース管理からビルド、テスト、デプロイまでの全工程を一元化します。複数ツールの統合管理、クラウドの利点の最大活用、環境の一貫性確保、セキュリティの統合的管理を実現します。',
    '当社のAWS認定専門家チームが、お客さまのニーズに合わせた最適なCodePipeline環境の設計から運用までをトータルでサポートします。',
  ],
}

const ArchitectureContents = {
  title: 'AWS CodePipeline',
  imgLink: ['/images/LP/LP202407/Architectures/architecture-awscodepipeline.png'],
}

const FeaturesContents = {
  title: 'AWS CodePipeline',
  items: [
    {
      num: '01',
      itemTitle: 'AWSサービスとの完全統合によるワンストップCI/CD',
      description:
        'CodePipelineとAWS Lambda、ECS、S3などのAWSサービスを緊密に連携させ、ソース管理からデプロイまでの全工程を一元化します。AitriaのAWS認定技術者が、お客さまの環境に最適化されたパイプラインを設計し、クラウドネイティブな開発環境を構築します。これにより、AWSの利点を最大限に活用した効率的なCI/CDプロセスを実現します。',
      src: '/images/LP/LP202407/img/img04.jpeg',
    },
    {
      num: '02',
      itemTitle: 'IaC（コードとしてのインフラストラクチャ）と一貫性のある環境構築',
      description:
        'CloudFormationとの連携により、インフラストラクチャのプロビジョニングを自動化します。豊富な導入実績に基づくベストプラクティスを適用し、コードによる一貫性のある環境構築と管理を実現します。これにより、環境構築の再現性が向上し、開発、テスト、本番環境間の一貫性が確保され、変更管理の効率が大幅に向上します。',
      src: '/images/LP/LP202407/img/img03.jpeg',
    },
    {
      num: '03',
      itemTitle: '拡張性と継続的な最適化',
      description:
        'AWS Organizationsとの連携により、複数のAWSアカウントやリージョンにまたがる安全で一貫性のあるデプロイを構築します。お客さまの組織構造に合わせたカスタマイズされたデプロイ戦略を提供し、大規模で複雑な環境でも効率的な運用を可能にします。さらに、AWS Well-Architectedフレームワークに基づく定期的な分析と最適化を実施し、最新のAWSベストプラクティスを適用しながら、安定したCI/CD環境の運用をバックアップします。',
      src: '/images/LP/LP202407/img/img05.jpeg',
    },
  ],
}

const FlowContents = [
  {
    num: 1,
    title: 'AWS環境の現状分析',
    description: 'お客さまのAWS利用状況と開発プロセスを詳細に分析します。',
    src: '/images/LP/LP202407/Icon/BsBarChartLine.svg',
  },
  {
    num: 2,
    title: 'クラウドネイティブCI/CD戦略の策定',
    description: 'AWS CodePipelineを中心とした最適なCI/CD戦略を立案します。',
    src: '/images/LP/LP202407/Icon/BsDiagram3.svg',
  },
  {
    num: 3,
    title: 'パイプライン設計と実装・運用支援',
    description:
      'CodePipeline環境を構築し、AWSサービスと連携した効率的なワークフローを実装します。また、継続的な運用改善をサポートします。',
    src: '/images/LP/LP202407/Icon/BsPersonCheck.svg',
  },
]

const FAQContents = [
  {
    num: 1,
    question: 'AWS CodePipelineは、既存のAWS以外のツールとも連携できますか？',
    answer:
      'はい、GitHubやBitbucketなどの外部ソース管理ツール、JenkinsなどのCI/CDツールとも連携可能です。最適な統合方法をご提案します。',
  },
  {
    num: 2,
    question: 'CodePipelineを導入すると、デプロイの速度はどの程度向上しますか？',
    answer:
      'プロジェクトの規模や複雑さにもよりますが、多くの場合、デプロイ時間を50%以上短縮できます。さらに、人的ミスの削減により、成功率も大幅に向上します。',
  },
  {
    num: 3,
    question: 'サポート体制はどのようになっていますか？',
    answer: '定期的なレビューと最適化提案により、常に最新かつ効率的なCI/CD環境を維持します。',
  },
  {
    num: 4,
    question: '小規模なチームでもCodePipelineは活用できますか？',
    answer:
      'はい、CodePipelineはスケーラブルな設計になっているため、小規模プロジェクトから大規模システムまで対応可能です。チームの規模に応じた最適な設定をサポートします。',
  },
  {
    num: 5,
    question: 'AWS CodePipeline導入支援サービスの料金体系はどうなっていますか？',
    answer:
      'お客さまのプロジェクト規模や要件に応じて、カスタマイズされた料金プランを提供しています。初期導入費用と月額サポート費用を組み合わせた柔軟な料金体系となっております。詳細は無料相談にてご案内します。',
  },
]

const inPocketList = [
  {
    image: '/images/inPocket/AWS CodePipeline/story_AWSCodePipeline.png',
    title:
      'AIで生成する物語-第4話：AWSサービスとのシームレスな統合を叶えるCI/CDサービス AWS CodePipelineの導入を検討してみた話',
    date: '2024.9.19',
    href: 'https://aitria.co.jp/in-pocket/11762',
  },
  {
    image: '/images/inPocket/AWS CodePipeline/knowledge_CodePipeline.png',
    title: 'AWS CodePipelineとは？AWS Codeシリーズにおける役割や特徴を解説',
    date: '2024.9.5',
    href: 'https://aitria.co.jp/in-pocket/11673',
  },
  {
    image: '/images/inPocket/AWS CodePipeline/cicd_point.png',
    title: 'CI/CDとは？CI/CDツールを選ぶポイント7選＆ツール比較！',
    date: '2024.8.23',
    href: 'https://aitria.co.jp/in-pocket/11532',
  },
]

const topics = [
  {
    title:
      '特徴量エンジニアリングを使って生成AIによるUIデザインと画面レイアウトのアウトプットをコントロールする技術を開発',
    date: '2024年9月6日',
    src: '/pressrelease/3726',
  },
  {
    title: 'アート引越センターが提供する、AIが自動見積りをするアプリの開発支援をいたしました',
    date: '2024年5月21日',
    src: '/pressrelease/3369',
  },
  { title: 'AWSセレクトティアサービスパートナーに認定', date: '2023年5月29日', src: '/pressrelease/2785' },
]

const CallToActionContents = {
  heading: ['AWS CodePipelineの導入で、', '開発プロセスを革新しませんか'],
}

const AWSCodePipeline: React.FC = () => {
  const contactRef = useRef<HTMLDivElement>(null)
  const contentsRef = useRef<HTMLDivElement>(null)

  useScrollHideContact(contactRef, contentsRef)
  return (
    <>
      <article className={styles.LPPage}>
        <div className={styles.contents} ref={contentsRef}>
          <Top contents={TopContents} paths={paths} />
          <Introduction contents={IntroductionContents} />
          <BestPractice contents={BestPracticeContents} />
          <Architecture contents={ArchitectureContents} />
          <Features contents={FeaturesContents} />
          <Flow contents={FlowContents} />
          <PriceList />
          <QA contents={FAQContents} />
          <NewsLp inPocketList={inPocketList} topics={topics} />
          <CallToAction contents={CallToActionContents} />
        </div>
        <div ref={contactRef} className={styles.contact_wrapper}>
          <Contact formId={ContactFormId} />
        </div>
        <div>
          <MailmagazineLink />
        </div>
      </article>
    </>
  )
}
export default AWSCodePipeline
